import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Upload, Lock, Calendar, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ImportWizard from '../components/ImportWizard';
import GuestProfile from '../components/GuestProfile';
import CheckOutDialog from '../components/CheckOutDialog';
import useConfig, { 
  FALLBACK_STATUS_LABELS, 
  FALLBACK_EVENT_TYPES, 
  FALLBACK_EVENT_STATUS, 
  FALLBACK_PAYMENT_METHODS
} from '../hooks/useConfig';

// Import all refactored components
import {
  formatCurrency,
  EVENT_ICONS,
  ReservationsFilters,
  ReservationsTable,
  ReservationFormDialog,
  ReservationDetailDialog,
  DepositDialog,
  BlockingDialog,
  SyncTab,
  CheckInDialog,
  PaymentDialog,
  EventDialog
} from '../components/reservations';

export default function ReservationsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Core state
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source: '', status: 'active', search: '' });
  const [guests, setGuests] = useState([]);
  const [channels, setChannels] = useState([]);
  
  // Dialog visibility states
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showBlockingDialog, setShowBlockingDialog] = useState(false);
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  
  // Selected items
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [viewingGuest, setViewingGuest] = useState(null);
  const [checkoutReservation, setCheckoutReservation] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('list');
  
  // Form states
  const [formData, setFormData] = useState({
    guest_id: '',
    source: 'DIRECT',
    platform_ref: '',
    check_in: '',
    check_out: '',
    adults: 1,
    children: 0,
    infants: 0,
    total_amount: '',
    currency: 'MAD',
    commission_rate: 0,
    commission_amount: 0,
    notes: '',
    tourist_tax_applicable: true,
    deposit_amount: '',
    deposit_date: ''
  });
  
  const [isNewGuest, setIsNewGuest] = useState(false);
  const [newGuestData, setNewGuestData] = useState({ first_name: '', last_name: '', email: '', phone: '', country: '' });
  const [blockingData, setBlockingData] = useState({ check_in: '', check_out: '', blocked_reason: '' });
  const [depositData, setDepositData] = useState({ deposit_amount: '', deposit_date: '', balance_reminder_date: '' });
  
  // Event state
  const [reservationEvents, setReservationEvents] = useState([]);
  const [eventForm, setEventForm] = useState({
    type: 'MEAL',
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    pax_count: 1,
    notes: ''
  });
  
  // Payment state
  const [paymentData, setPaymentData] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({ total_amount: 0, total_paid: 0, remaining: 0 });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    currency: 'MAD',
    method: 'CASH',
    payment_type: 'DEPOSIT',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // System currencies for payment dialog
  const [systemCurrencies, setSystemCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  
  // Sync state
  const [icalMissingCount, setIcalMissingCount] = useState(0);
  const [missingReservations, setMissingReservations] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [lastSyncCheck, setLastSyncCheck] = useState(null);
  
  // Load configuration dynamically
  const { options: configOptions, loading: configLoading } = useConfig([
    'RESERVATION_STATUS', 'EVENT_TYPE', 'EVENT_STATUS', 'PAYMENT_METHOD', 'CURRENCY', 'CHANNEL'
  ]);
  
  // Check if user can see financial info
  const canSeeFinancials = user?.permissions?.includes('finance.view') || 
                           user?.role?.toLowerCase() === 'propriétaire' ||
                           user?.role?.toLowerCase() === 'owner';
  
  // Build dynamic maps with fallbacks
  const statusLabels = useMemo(() => {
    const base = { ...FALLBACK_STATUS_LABELS };
    if (!configLoading && configOptions.RESERVATION_STATUS) {
      configOptions.RESERVATION_STATUS.forEach(o => {
        base[o.code] = o.label;
      });
    }
    base.FINISHED = base.CHECKED_OUT || 'Terminée';
    return base;
  }, [configLoading, configOptions.RESERVATION_STATUS]);
  
  const eventTypeOptions = useMemo(() => {
    if (!configLoading && configOptions.EVENT_TYPE?.length > 0) {
      return configOptions.EVENT_TYPE.map(o => ({
        value: o.code,
        label: o.label,
        icon: EVENT_ICONS[o.code] || EVENT_ICONS.OTHER,
        color: `bg-opacity-10 text-opacity-100`,
        rawColor: o.color
      }));
    }
    return Object.entries(FALLBACK_EVENT_TYPES).map(([value, label]) => ({
      value, label, icon: EVENT_ICONS[value] || EVENT_ICONS.OTHER, color: 'bg-gray-100 text-gray-700'
    }));
  }, [configLoading, configOptions.EVENT_TYPE]);
  
  const paymentMethods = useMemo(() => {
    if (!configLoading && configOptions.PAYMENT_METHOD?.length > 0) {
      return configOptions.PAYMENT_METHOD.map(o => ({ value: o.code, label: o.label }));
    }
    return [
      { value: 'CASH', label: 'Espèces' },
      { value: 'CARD', label: 'Carte bancaire' },
      { value: 'TRANSFER', label: 'Virement' },
      { value: 'CHECK', label: 'Chèque' },
      { value: 'OTHER', label: 'Autre' }
    ];
  }, [configLoading, configOptions.PAYMENT_METHOD]);

  // ============ EFFECTS ============
  
  // Fetch channel configuration
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await api.get('/config?category=CHANNEL');
        const channelOptions = response.data?.CHANNEL?.options || [];
        setChannels(channelOptions.filter(c => c.is_active));
      } catch (error) {
        console.error('Failed to fetch channels:', error);
        // Fallback minimal - pas de hardcode des valeurs spécifiques
        setChannels([]);
      }
    };
    fetchChannels();
  }, []);

  // Fetch system currencies for payment dialog
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await api.get('/currencies');
        setSystemCurrencies(res.data.currencies || []);
        // Build exchange rates map
        const rates = {};
        (res.data.currencies || []).forEach(c => {
          rates[c.code] = c.rate || 1;
        });
        setExchangeRates(rates);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
        // Fallback
        setSystemCurrencies([
          { code: 'MAD', label: 'Dirham', symbol: 'DH', rate: 1 },
          { code: 'EUR', label: 'Euro', symbol: '€', rate: 10.77 },
          { code: 'USD', label: 'Dollar', symbol: '$', rate: 9.40 }
        ]);
        setExchangeRates({ MAD: 1, EUR: 10.77, USD: 9.40 });
      }
    };
    fetchCurrencies();
  }, []);

  // Handle URL params to open reservation detail
  useEffect(() => {
    const reservationId = searchParams.get('id');
    if (reservationId && reservations.length > 0) {
      const reservation = reservations.find(r => r.id === parseInt(reservationId));
      if (reservation) {
        setSelectedReservation(reservation);
        setShowDetailDialog(true);
      } else {
        api.get(`/reservations/${reservationId}`)
          .then(res => {
            setSelectedReservation(res.data);
            setShowDetailDialog(true);
          })
          .catch(() => toast.error('Réservation non trouvée'));
      }
    }
  }, [searchParams, reservations]);

  // Fetch reservations and guests when filters change
  useEffect(() => {
    fetchReservations();
    fetchGuests();
  }, [filters]);

  // Fetch iCal sync status
  useEffect(() => {
    const fetchIcalStatus = async () => {
      try {
        const response = await api.get('/ical/status');
        setIcalMissingCount(response.data.missingCount || 0);
        setLastSyncCheck(response.data.lastCheck);
      } catch (error) {
        console.log('iCal status not available');
      }
    };
    fetchIcalStatus();
  }, []);

  // Fetch events and payments when opening reservation detail
  useEffect(() => {
    if (selectedReservation && showDetailDialog) {
      fetchReservationEvents(selectedReservation.id);
      // Fetch payments for non-OTA channels
      const channelConfig = channels.find(c => c.code === selectedReservation.source);
      const isOta = channelConfig?.metadata?.is_ota === true;
      if (!isOta) {
        fetchReservationPayments(selectedReservation.id);
      }
    }
  }, [selectedReservation, showDetailDialog, channels]);

  // ============ DATA FETCHING ============
  
  const fetchReservations = async () => {
    try {
      const response = await api.get('/reservations', { params: filters });
      setReservations(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await api.get('/guests', { params: { limit: 100 } });
      setGuests(response.data.data);
    } catch (error) {
      console.error('Failed to fetch guests');
    }
  };

  const fetchReservationEvents = async (reservationId) => {
    try {
      const response = await api.get(`/reservations/${reservationId}/events`);
      setReservationEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchReservationPayments = async (reservationId) => {
    try {
      const response = await api.get(`/reservations/${reservationId}/payments`);
      setPaymentData(response.data.payments || []);
      setPaymentSummary({
        total_amount: response.data.total_amount || 0,
        total_paid: response.data.total_paid || 0,
        remaining: response.data.remaining || 0,
        is_fully_paid: response.data.is_fully_paid || false
      });
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  };

  // ============ HANDLERS ============
  
  const resetForm = () => {
    setFormData({
      guest_id: '',
      source: 'DIRECT',
      platform_ref: '',
      check_in: '',
      check_out: '',
      adults: 1,
      children: 0,
      infants: 0,
      total_amount: '',
      currency: 'MAD',
      commission_rate: 0,
      notes: ''
    });
    setSelectedReservation(null);
    setIsNewGuest(false);
    setNewGuestData({ first_name: '', last_name: '', email: '', phone: '', country: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let guestId = formData.guest_id;
      
      if (isNewGuest) {
        if (!newGuestData.first_name || !newGuestData.last_name) {
          toast.error('Nom et prénom du client requis');
          return;
        }
        const guestResponse = await api.post('/guests', newGuestData);
        guestId = guestResponse.data.id;
        toast.success('Client créé');
        fetchGuests();
      }
      
      if (!guestId) {
        toast.error('Veuillez sélectionner ou créer un client');
        return;
      }
      
      const submitData = { ...formData, guest_id: guestId };
      
      if (selectedReservation) {
        await api.put(`/reservations/${selectedReservation.id}`, submitData);
        toast.success('Réservation mise à jour');
      } else {
        await api.post('/reservations', submitData);
        toast.success('Réservation créée');
      }
      setShowDialog(false);
      resetForm();
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success('Réservation supprimée');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/reservations/${id}/status`, { status });
      toast.success('Statut mis à jour');
      fetchReservations();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openEditDialog = (reservation) => {
    setSelectedReservation(reservation);
    setFormData({
      guest_id: reservation.guest_id,
      source: reservation.source,
      platform_ref: reservation.platform_ref || '',
      check_in: reservation.check_in,
      check_out: reservation.check_out,
      adults: reservation.adults,
      children: reservation.children,
      infants: reservation.infants,
      total_amount: reservation.total_amount,
      currency: reservation.currency,
      commission_rate: reservation.commission_rate,
      notes: reservation.notes || '',
      tourist_tax_applicable: reservation.tourist_tax_applicable !== false,
      deposit_amount: reservation.deposit_amount || '',
      deposit_date: reservation.deposit_date || ''
    });
    setShowDialog(true);
  };

  // Blocking handlers
  const handleCreateBlocking = async () => {
    try {
      await api.post('/reservations/blocking', blockingData);
      toast.success('Dates bloquées avec succès');
      setShowBlockingDialog(false);
      setBlockingData({ check_in: '', check_out: '', blocked_reason: '' });
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Deposit handlers
  const openDepositDialog = (reservation) => {
    setSelectedReservation(reservation);
    setDepositData({
      deposit_amount: reservation.deposit_amount || '',
      deposit_date: reservation.deposit_date || '',
      balance_reminder_date: reservation.balance_reminder_date || ''
    });
    setShowDepositDialog(true);
  };

  const handleUpdateDeposit = async (createPayment = false) => {
    if (!selectedReservation) return;
    try {
      const response = await api.put(`/reservations/${selectedReservation.id}/deposit`, {
        ...depositData,
        create_payment: createPayment
      });
      
      if (createPayment && response.data.new_payment) {
        toast.success('Acompte validé et paiement créé');
      } else {
        toast.success('Acompte mis à jour');
      }
      
      setShowDepositDialog(false);
      setDepositData({ deposit_amount: '', deposit_date: '', balance_reminder_date: '', create_payment: false });
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Event handlers
  const openEventDialog = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setEventForm({
        type: event.type,
        title: event.title,
        description: event.description || '',
        event_date: event.event_date || '',
        event_time: event.event_time || '',
        amount: event.amount || '',
        pax_count: event.pax_count || 1,
        notes: event.notes || '',
        status: event.status || 'PENDING'
      });
    } else {
      setSelectedEvent(null);
      setEventForm({
        type: 'MEAL',
        title: '',
        description: '',
        event_date: selectedReservation?.check_in || '',
        event_time: '',
        amount: '',
        pax_count: selectedReservation?.total_pax || 1,
        notes: '',
        status: 'PENDING'
      });
    }
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title) {
      toast.error('Titre requis');
      return;
    }
    try {
      if (selectedEvent) {
        await api.put(`/reservations/events/${selectedEvent.id}`, eventForm);
        toast.success('Événement mis à jour');
      } else {
        await api.post(`/reservations/${selectedReservation.id}/events`, eventForm);
        toast.success('Événement ajouté');
      }
      setShowEventDialog(false);
      fetchReservationEvents(selectedReservation.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await api.delete(`/reservations/events/${eventId}`);
      toast.success('Événement supprimé');
      fetchReservationEvents(selectedReservation.id);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Payment handlers
  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    try {
      const response = await api.post(`/reservations/${selectedReservation.id}/payments`, paymentForm);
      toast.success('Paiement enregistré');
      setPaymentData([...paymentData, response.data.payment]);
      setPaymentSummary(response.data.summary);
      setShowPaymentDialog(false);
      setPaymentForm({
        amount: '',
        method: 'CASH',
        payment_type: 'DEPOSIT',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await api.delete(`/reservations/${selectedReservation.id}/payments/${paymentId}`);
      toast.success('Paiement supprimé');
      fetchReservationPayments(selectedReservation.id);
      fetchReservations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openPaymentDialog = () => {
    setPaymentForm({
      amount: paymentSummary.remaining.toString(),
      method: 'CASH',
      payment_type: paymentSummary.total_paid > 0 ? 'BALANCE' : 'DEPOSIT',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowPaymentDialog(true);
  };

  // Sync handlers
  const runIcalSync = async () => {
    setSyncLoading(true);
    try {
      const response = await api.post('/ical/check');
      setMissingReservations(response.data.missing || []);
      setIcalMissingCount(response.data.missing?.length || 0);
      setLastSyncCheck(response.data.lastCheck);
      toast.success('Synchronisation terminée');
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncLoading(false);
    }
  };

  const createFromMissing = (missing) => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      check_in: new Date(missing.check_in).toISOString().slice(0, 10),
      check_out: new Date(missing.check_out).toISOString().slice(0, 10),
      source: missing.sources?.includes('Booking') ? 'BOOKING' : 
              missing.sources?.includes('Airbnb') ? 'AIRBNB' : 'DIRECT',
      notes: `Créé depuis calendrier externe: ${missing.source || missing.sources?.join(', ')}`
    }));
    setShowDialog(true);
  };

  // Import handler
  const handleImportSuccess = () => {
    fetchReservations();
  };

  // ============ RENDER ============
  
  // If viewing a guest profile, show the profile component
  if (viewingGuest) {
    return (
      <GuestProfile 
        guest={viewingGuest}
        onBack={() => setViewingGuest(null)}
        onGuestUpdate={(updated) => {
          setViewingGuest(updated);
          fetchReservations();
        }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Réservations</h1>
          <p className="text-muted-foreground mt-1">Gestion des séjours</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBlockingDialog(true)} data-testid="block-dates-btn">
            <Lock className="w-4 h-4 mr-2" />
            Bloquer dates
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importer
          </Button>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-reservation-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle réservation
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list">
            <Calendar className="w-4 h-4 mr-2" />
            Liste des réservations
          </TabsTrigger>
          <TabsTrigger value="sync" className="relative">
            <RefreshCw className="w-4 h-4 mr-2" />
            Synchronisation
            {icalMissingCount > 0 && (
              <Badge variant="destructive" className="ml-2">{icalMissingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tab: Reservations List */}
        <TabsContent value="list" className="space-y-4">
          <ReservationsFilters
            filters={filters}
            setFilters={setFilters}
            channels={channels}
            statusOptions={configOptions.RESERVATION_STATUS || []}
          />
          
          <ReservationsTable
            reservations={reservations}
            loading={loading}
            channels={channels}
            statusLabels={statusLabels}
            statusOptions={configOptions.RESERVATION_STATUS || []}
            canSeeFinancials={canSeeFinancials}
            onViewDetails={(reservation) => {
              setSelectedReservation(reservation);
              setShowDetailDialog(true);
            }}
            onEdit={openEditDialog}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onManageDeposit={openDepositDialog}
            onCheckIn={(reservation) => {
              setSelectedReservation(reservation);
              setShowCheckinDialog(true);
            }}
          />
        </TabsContent>

        {/* Tab: Synchronization */}
        <TabsContent value="sync" className="space-y-4">
          <SyncTab
            missingReservations={missingReservations}
            syncLoading={syncLoading}
            lastSyncCheck={lastSyncCheck}
            onSync={runIcalSync}
            onCreateFromMissing={createFromMissing}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ReservationFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        selectedReservation={selectedReservation}
        formData={formData}
        setFormData={setFormData}
        channels={channels}
        guests={guests}
        isNewGuest={isNewGuest}
        setIsNewGuest={setIsNewGuest}
        newGuestData={newGuestData}
        setNewGuestData={setNewGuestData}
        onSubmit={handleSubmit}
      />

      <ImportWizard 
        open={showImportDialog} 
        onOpenChange={setShowImportDialog}
        onSuccess={handleImportSuccess}
      />

      <ReservationDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        reservation={selectedReservation}
        channels={channels}
        statusLabels={statusLabels}
        paymentSummary={paymentSummary}
        paymentData={paymentData}
        paymentMethods={paymentMethods}
        reservationEvents={reservationEvents}
        eventTypeOptions={eventTypeOptions}
        canSeeFinancials={canSeeFinancials}
        onEdit={() => {
          setShowDetailDialog(false);
          openEditDialog(selectedReservation);
        }}
        onCheckout={() => {
          setCheckoutReservation(selectedReservation);
          setShowDetailDialog(false);
          setShowCheckoutDialog(true);
        }}
        onAddPayment={openPaymentDialog}
        onDeletePayment={handleDeletePayment}
        onAddEvent={() => openEventDialog()}
        onEditEvent={openEventDialog}
        onDeleteEvent={handleDeleteEvent}
        onRefresh={fetchReservations}
      />

      <BlockingDialog
        open={showBlockingDialog}
        onOpenChange={setShowBlockingDialog}
        blockingData={blockingData}
        setBlockingData={setBlockingData}
        onSubmit={handleCreateBlocking}
      />

      <DepositDialog
        open={showDepositDialog}
        onOpenChange={setShowDepositDialog}
        reservation={selectedReservation}
        depositData={depositData}
        setDepositData={setDepositData}
        onSave={handleUpdateDeposit}
      />

      <CheckOutDialog
        reservation={checkoutReservation}
        open={showCheckoutDialog}
        onOpenChange={setShowCheckoutDialog}
        onSuccess={() => {
          fetchReservations();
          setCheckoutReservation(null);
        }}
      />

      <EventDialog
        open={showEventDialog}
        onOpenChange={setShowEventDialog}
        selectedEvent={selectedEvent}
        eventForm={eventForm}
        setEventForm={setEventForm}
        eventTypeOptions={eventTypeOptions}
        onSave={handleSaveEvent}
      />

      <PaymentDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        reservation={selectedReservation}
        paymentSummary={paymentSummary}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        paymentMethods={paymentMethods}
        currencies={systemCurrencies}
        exchangeRates={exchangeRates}
        onSave={handleAddPayment}
      />

      <CheckInDialog
        open={showCheckinDialog}
        onOpenChange={setShowCheckinDialog}
        reservation={selectedReservation}
        onComplete={() => {
          fetchReservations();
          setSelectedReservation(null);
        }}
        onRefresh={fetchReservations}
      />
    </div>
  );
}
